import isEqual from 'lodash/isEqual';
import { fetchUserDetails, logout, updateUserEmail } from '../../api/user';
import { createAddress, fetchCities, fetchPickupLocations, removeAddress } from '../../api/locations';
import { fetchCityConfigs, fetchMerchantConfig } from '../../api/configs';
import { locationNameNullFallback, locationsToUniqueCityIds } from '../../api/transformers/locations';
import i18n from '../../i18next';
import { loginWithPrompt, removeTokenDetails } from '../../utils/auth';
import {
  DEFAULT_CITY_ID_KEY,
  DEFAULT_PAYMENT_METHOD_ID_KEY,
  DEFAULT_PICK_UP_LOCATION_ID_KEY,
  SECONDARY_PAYMENT_METHOD_ID_KEY,
  WALLET_PAYMENT_METHOD_ID,
  MERCHANT_CONFIG_FETCHING_INTERVAL_MIN
} from '../../utils/constants';
import { getItem, setItem } from '../../utils/localStorage';
import {
  fetchUserCreditCards,
  fetchUserCreditCardsTransporter,
  fetchUserInvoices,
  fetchUserInvoicesTransporter,
  fetchUserWalletConfig,
  setDefaultPaymentMethodUDS,
  setPaymentMethodsUDS,
  setSecondaryPaymentMethodUDS
} from '../../api/payments';
import { publishEventEffect } from '../../utils/analytika/reporterAnalytika';
import { getGalileoFlags } from '../../utils/galileo/GalileoProvider';
import {
  genericNetworkErrorSample,
  genericPaymentMessage,
  merchantConfigFetchingCfdScheduled,
  primaryPaymentMethodNotPresent,
  secondaryPaymentMethodNotPresent
} from '../../utils/analytika/events';
import { message } from 'antd';
import i18next from '../../i18next';
import { ANALYTIKA_API_NAMES, ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { PaymentType } from '../../api/models/payments';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import getOrCreateDeviceId from '../../utils/deviceId';

const initialState = {
  userSettings: {
    notifyWhenDeliveryIsCreated: true,
    defaultLanguage: i18n.language || 'en'
  },
  selectedPickupLocation: undefined,
  defaultPickupLocation: undefined,
  defaultPaymentMethodId: undefined,
  secondaryPaymentMethodId: undefined,
  isWalletCreditUsed: undefined,
  pickupLocations: [],
  configByCityId: {},
  paymentMethods: [],
  cities: [],
  userData: {},
  loadCompleteFor: {
    pickupLocations: false,
    cities: false,
    blockingRequests: false,
    galileoVars: false
  },
  isUserAuthorized: false,
  userWalletConfig: {
    isZeroWalletBalance: false,
    userBalance: {
      amount: undefined,
      currency: undefined,
      fractionDigits: undefined
    }
  },
  userGalileoVars: {
    [FeatureFlag.IS_OTP_TOGGLING_ENABLED]: undefined,
    [FeatureFlag.ORDER_TYPE_PER_MERCHANT]: 'LMD',
    [FeatureFlag.IS_ACCESS_TYPE_CONTROLLED]: undefined,
    [FeatureFlag.IS_ACCOUNT_ACTIVATION_ENABLED]: undefined,
    [FeatureFlag.IS_SEGMENTATION_ENABLED]: undefined,
    [FeatureFlag.IS_EMAIL_VERIFICATION_ENABLED]: undefined,
    [FeatureFlag.IS_SCHEDULED_ENABLED]: undefined,
    [FeatureFlag.IS_ESTIMATES_ENABLED]: undefined,
    [FeatureFlag.IS_ZONE_VALIDATION_ENABLED]: undefined,
    [FeatureFlag.IS_TRANSPORTER_TRACKING_ENABLED]: undefined,
    [FeatureFlag.IS_NEW_INVOICES_API_ENABLED]: undefined,
    [FeatureFlag.IS_NEW_CARDS_API_ENABLED]: undefined,
    [FeatureFlag.IS_LOCATIONS_REVERSE_GEOCODING_DISABLED]: undefined,
    [FeatureFlag.IS_ONE_CLICK_DISABLED]: undefined
  },
  merchantConfig: {
    preferredContactNumber: undefined,
    isMerchantEnabled: true,
    isOtpEnabled: true,
    isCustomerPhoneNumberMandatory: false,
    isDropOffMandatory: false,
    isCustomFulfillmentDistanceActive: false,
    standardFulfillmentDistance: undefined,
    customFulfillmentDistance: undefined,
    cfdEndsAt: null,
    availablePaymentMethods: {
      isInvoiceEnabled: false,
      isCardEnabled: false,
      isWalletEnabled: false
    },
    primaryPaymentMethod: null,
    secondaryPaymentMethod: null,
    fulfillmentTypes: ['ON_DEMAND', 'DEDICATED'],
    accessTypes: {
      isApiAccessEnabled: true,
      isPortalAccessEnabled: true
    },
    isOrderIdMandatory: false
  }
};

export const user = {
  state: initialState,
  selectors: slice => ({
    defaultCityConfig() {
      return slice(state => {
        return state.configByCityId[state.defaultPickupLocation?.city_id];
      });
    }
  }),
  reducers: {
    setUserData(state, userData) {
      return {
        ...state,
        userData
      };
    },
    updateUserSettings(state, userSettings) {
      return {
        ...state,
        userSettings: {
          ...state.userSettings,
          ...userSettings
        }
      };
    },
    setPickupLocations(state, pickupLocations) {
      return { ...state, pickupLocations, loadCompleteFor: { ...state.loadCompleteFor, pickupLocations: true } };
    },

    setDefaultPickupLocation(state, defaultPickupLocation) {
      return { ...state, defaultPickupLocation };
    },

    setSelectedPickupLocation(state, selectedPickupLocation) {
      return { ...state, selectedPickupLocation };
    },

    setCityConfigs(state, configByCityId = {}) {
      return {
        ...state,
        configByCityId
      };
    },

    setPaymentMethods(state, paymentMethods = []) {
      return {
        ...state,
        paymentMethods
      };
    },

    setDefaultPaymentMethodId(state, defaultPaymentMethodId) {
      return {
        ...state,
        defaultPaymentMethodId
      };
    },
    setSecondaryPaymentMethodId(state, secondaryPaymentMethodId) {
      return {
        ...state,
        secondaryPaymentMethodId
      };
    },
    setIsWalletCreditUsedProp(state, isWalletCreditUsed) {
      return {
        ...state,
        isWalletCreditUsed
      };
    },
    setCities(state, cities) {
      return {
        ...state,
        cities,
        loadCompleteFor: { ...state.loadCompleteFor, cities: true }
      };
    },
    setUserWalletConfig(state, userWalletConfig) {
      return {
        ...state,
        userWalletConfig
      };
    },
    setIsUserAuthorized(state, isUserAuthorized) {
      return {
        ...state,
        isUserAuthorized
      };
    },
    setLoadCompleteForBlockingRequests(state) {
      return {
        ...state,
        loadCompleteFor: { ...state.loadCompleteFor, blockingRequests: true }
      };
    },
    setUserGalileoVars(state, userGalileoVars) {
      return {
        ...state,
        userGalileoVars,
        loadCompleteFor: { ...state.loadCompleteFor, galileoVars: true }
      };
    },
    setMerchantConfig(state, merchantConfig) {
      return {
        ...state,
        merchantConfig
      };
    }
  },
  effects: {
    async fetchAvailablePaymentMethods(_, state) {
      const {
        user: {
          merchantConfig: { availablePaymentMethods },
          userGalileoVars: {
            [FeatureFlag.IS_NEW_INVOICES_API_ENABLED]: isNewInvoicesApiEnabled,
            [FeatureFlag.IS_NEW_CARDS_API_ENABLED]: isNewCardsApiEnabled
          }
        }
      } = state;

      const fetchInvoicesFunction = isNewInvoicesApiEnabled ? fetchUserInvoicesTransporter : fetchUserInvoices;
      const fetchCardsFunction = isNewCardsApiEnabled ? fetchUserCreditCardsTransporter : fetchUserCreditCards;
      let { isInvoiceEnabled, isCardEnabled } = availablePaymentMethods;

      const fetchPaymentsCalls = [];

      isInvoiceEnabled && fetchPaymentsCalls.push(fetchInvoicesFunction());
      isCardEnabled && fetchPaymentsCalls.push(fetchCardsFunction());

      const results = await Promise.all(fetchPaymentsCalls);

      this.setPaymentMethods(results.flat());
    },

    // TODO: deprecate after UDS release for all users
    async checkSyncPaymentsMethods(_, state) {
      const {
        user: {
          merchantConfig: { primaryPaymentMethod, secondaryPaymentMethod, availablePaymentMethods },
          paymentMethods
        }
      } = state;

      const { isInvoiceEnabled, isCardEnabled, isWalletEnabled } = availablePaymentMethods;

      const secondaryMethodIdLocalStorage = parseInt(getItem(SECONDARY_PAYMENT_METHOD_ID_KEY));
      const primaryMethodIdLocalStorage = parseInt(getItem(DEFAULT_PAYMENT_METHOD_ID_KEY));

      // auto-setting invoice for invoice clients
      if (primaryPaymentMethod === null && isInvoiceEnabled) {
        const invoicePaymentMethodId = paymentMethods.find(
          paymentMethod => paymentMethod.paymentType === PaymentType.INVOICE
        )?.id;
        await this.updateDefaultPaymentMethodUDS(invoicePaymentMethodId);
        const paymentMsg = `Primary payment method INVOICE ${primaryMethodIdLocalStorage} moved to UDS`;
        this.publishAnalytikaEvent(genericPaymentMessage(paymentMsg));
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PAYMENT_MESSAGE,
          mesage: paymentMsg,
          event_version: 2
        });
      }

      // auto-setting wallet for wallet clients
      if (primaryPaymentMethod === null && !isInvoiceEnabled && isWalletEnabled) {
        await this.updateDefaultPaymentMethodUDS(WALLET_PAYMENT_METHOD_ID);
        const paymentMsg = `Primary payment method WALLET ${primaryMethodIdLocalStorage} moved from local storage to UDS`;
        this.publishAnalytikaEvent(genericPaymentMessage(paymentMsg));
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PAYMENT_MESSAGE,
          mesage: paymentMsg,
          event_version: 2
        });
      }

      // auto-setting secondary method for wallet clients - if they had it prev in local storage
      if (
        secondaryPaymentMethod === null &&
        !isInvoiceEnabled &&
        isWalletEnabled &&
        isCardEnabled &&
        secondaryMethodIdLocalStorage
      ) {
        await this.updateSecondaryPaymentMethodUDS(secondaryMethodIdLocalStorage);
        const paymentMsg = `Secondary payment method ${secondaryMethodIdLocalStorage} moved from local storage to UDS`;
        this.publishAnalytikaEvent(genericPaymentMessage(paymentMsg)); // auto-setting secondary method for wallet clients - if they had it prev in local storage
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PAYMENT_MESSAGE,
          mesage: paymentMsg,
          event_version: 2
        });
      } else if (
        secondaryPaymentMethod === null &&
        !isInvoiceEnabled &&
        isWalletEnabled &&
        isCardEnabled &&
        !secondaryMethodIdLocalStorage
      ) {
        const firstExistingCardPaymentMethodId = paymentMethods.find(
          paymentMethod => paymentMethod.paymentType === PaymentType.CREDIT_CARD
        )?.id;
        await this.updateSecondaryPaymentMethodUDS(firstExistingCardPaymentMethodId);
        const paymentMsg = `Secondary payment method CARD ${secondaryMethodIdLocalStorage} updated in UDS`;
        this.publishAnalytikaEvent(genericPaymentMessage(paymentMsg));
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PAYMENT_MESSAGE,
          mesage: paymentMsg,
          event_version: 2
        });
      }
    },

    assignPaymentMethods(_, state) {
      const {
        user: {
          merchantConfig: {
            primaryPaymentMethod,
            secondaryPaymentMethod,
            availablePaymentMethods: { isWalletEnabled }
          },
          paymentMethods
        }
      } = state;

      // part of backend business logics - zero ID means wallet is default
      const isWalletCreditUsed = isWalletEnabled && primaryPaymentMethod === WALLET_PAYMENT_METHOD_ID;

      if (isWalletCreditUsed) {
        this.setIsWalletCreditUsedProp(true);
        this.setDefaultPaymentMethodId(WALLET_PAYMENT_METHOD_ID);
      } else {
        this.setIsWalletCreditUsedProp(false);
        const isPrimaryPaymentMethodStored = !!paymentMethods.find(
          paymentMethod => paymentMethod.id === primaryPaymentMethod
        );
        if (isPrimaryPaymentMethodStored) {
          this.setDefaultPaymentMethodId(primaryPaymentMethod);
        } else if (!isPrimaryPaymentMethodStored && primaryPaymentMethod) {
          this.publishAnalytikaEvent(primaryPaymentMethodNotPresent(primaryPaymentMethod));
          publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PRIMARY_MISSING, event_version: 2 });
        }
      }

      const isSecondaryPaymentMethodStored = !!paymentMethods.find(
        paymentMethod => paymentMethod.id === secondaryPaymentMethod
      );
      if (isSecondaryPaymentMethodStored) {
        this.setSecondaryPaymentMethodId(secondaryPaymentMethod);
      } else if (!isSecondaryPaymentMethodStored && secondaryPaymentMethod) {
        this.publishAnalytikaEvent(secondaryPaymentMethodNotPresent(secondaryPaymentMethod));
        publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_SECOND_MISSING, event_version: 2 });
      }
    },

    async fetchPaymentMethods() {
      await this.fetchAvailablePaymentMethods();
      await this.checkSyncPaymentsMethods();
      await this.assignPaymentMethods();
    },

    async updateDefaultPaymentMethodUDS(methodId) {
      const merchantConfig = await setDefaultPaymentMethodUDS({ primary_payment_method: methodId });
      this.setMerchantConfig(merchantConfig);
      this.assignPaymentMethods();
    },

    async updateSecondaryPaymentMethodUDS(methodId) {
      const merchantConfig = await setSecondaryPaymentMethodUDS({ secondary_payment_method: methodId });
      this.setMerchantConfig(merchantConfig);
      this.assignPaymentMethods();
    },

    async updateBothPaymentMethodsUDS({ primaryMethodId, secondaryMethodId }) {
      const merchantConfig = await setPaymentMethodsUDS({
        primary_payment_method: primaryMethodId,
        secondary_payment_method: secondaryMethodId
      });
      this.setMerchantConfig(merchantConfig);
      this.assignPaymentMethods();
    },

    async fetchUserDetails() {
      const userData = await fetchUserDetails();

      this.setUserData(userData);
    },

    async updateUserEmail(email) {
      return updateUserEmail(email);
    },

    storeDefaultPickupLocation(defaultPickupLocation) {
      this.setDefaultPickupLocation(defaultPickupLocation);
      setItem(DEFAULT_PICK_UP_LOCATION_ID_KEY, defaultPickupLocation.id);
      setItem(DEFAULT_CITY_ID_KEY, defaultPickupLocation.city_id);
      this.sortPickupLocationsStartWithDefault(defaultPickupLocation);
    },

    sortPickupLocationsStartWithDefault(defaultPickupLocation, appState) {
      const {
        user: { pickupLocations }
      } = appState;

      const sortedLocations = pickupLocations.sort((currentItem, nextItem) => {
        const currentName = currentItem?.nickname || currentItem?.name || '';
        const nextName = nextItem?.nickname || nextItem?.name || '';
        return currentName.localeCompare(nextName);
      });
      sortedLocations.splice(
        sortedLocations.findIndex(current => current.id === defaultPickupLocation.id),
        1
      );
      sortedLocations.unshift(defaultPickupLocation);

      this.setPickupLocations(locationNameNullFallback(sortedLocations));
    },

    async fetchPickupLocations() {
      const pickupLocations = await fetchPickupLocations();
      this.setPickupLocations(locationNameNullFallback(pickupLocations));

      if (pickupLocations && pickupLocations.length) {
        const storedDefaultPickupLocationId = parseInt(getItem(DEFAULT_PICK_UP_LOCATION_ID_KEY));
        const storedDefaultPickupLocation = pickupLocations.find(
          location => location.id === storedDefaultPickupLocationId
        );

        const selectedPickupLocation = storedDefaultPickupLocation || pickupLocations[0];

        this.storeDefaultPickupLocation(selectedPickupLocation);
        this.selectPickupLocation(selectedPickupLocation);
      }

      return pickupLocations;
    },

    changeLanguage(defaultLanguage) {
      this.updateUserSettings({
        defaultLanguage
      });
      i18n.changeLanguage(defaultLanguage);
    },

    async logoutUser() {
      await logout();
      this.setUserData({});
      removeTokenDetails();
      loginWithPrompt();
    },

    selectPickupLocation(selectedPickupLocation) {
      this.setSelectedPickupLocation(selectedPickupLocation);
    },

    async fetchDefaultCityConfig() {
      const defaultCityId = parseInt(getItem(DEFAULT_CITY_ID_KEY));
      if (defaultCityId) {
        const cityConfigs = await fetchCityConfigs([defaultCityId]);
        await this.setCityConfigs(cityConfigs);
      } else {
        await this.fetchCityConfigs();
      }
    },

    async fetchCityConfigs() {
      const [pickupLocations, cities] = await Promise.all([this.fetchPickupLocations(), fetchCities()]);
      const cityIds = locationsToUniqueCityIds(pickupLocations, cities);
      const cityConfigs = await fetchCityConfigs(cityIds);
      this.setCityConfigs(cityConfigs);
    },

    async fetchCities() {
      const cities = await fetchCities();
      this.setCities(cities);
    },

    async createAddress(data) {
      const { setAsDefault, ...requestData } = data;
      const { place_id: placeId } = await createAddress(requestData);
      const locations = await this.fetchPickupLocations();
      const newPickup = locations.find(current => `${current.placeId}` === `${placeId}`);
      if (setAsDefault && locations && locations.length) {
        await this.storeDefaultPickupLocation(newPickup);
      }
      return newPickup;
    },

    async removeAddress(addressId) {
      await removeAddress(addressId);
      await this.fetchPickupLocations();
    },

    async fetchUserWalletConfig() {
      const userWalletConfig = await fetchUserWalletConfig();
      this.setUserWalletConfig(userWalletConfig);
    },

    async fetchUserGalileoVars(userId) {
      const expressDeviceId = getOrCreateDeviceId();
      const featureFlags = await getGalileoFlags({ user: userId, device: expressDeviceId });
      this.setUserGalileoVars(featureFlags);
    },

    publishAnalytikaEvent(value, appState) {
      publishEventEffect(value, appState);
    },

    async fetchMerchantConfig(_, state) {
      const {
        user: { merchantConfig: prevMerchantConfig }
      } = state;
      let fetchedMerchantConfig;

      try {
        fetchedMerchantConfig = await fetchMerchantConfig();
      } catch (err) {
        fetchedMerchantConfig = prevMerchantConfig;
        message.error({
          content: `${i18next.t('general.networkError')}`
        });
        this.publishAnalytikaEvent(genericNetworkErrorSample(err, ANALYTIKA_API_NAMES.MERCHANT_CONFIG));
        return;
      }
      const { cfdEndsAt, isCustomFulfillmentDistanceActive } = fetchedMerchantConfig;
      if (!isEqual(prevMerchantConfig, fetchedMerchantConfig)) {
        this.setMerchantConfig(fetchedMerchantConfig);
      }
      const configRefreshInterval = MERCHANT_CONFIG_FETCHING_INTERVAL_MIN * 60 * 1000;

      if (isCustomFulfillmentDistanceActive) {
        const timeDiff = cfdEndsAt - new Date().getTime();
        const refreshInMs = timeDiff > configRefreshInterval ? configRefreshInterval : timeDiff;
        setTimeout(async () => {
          await this.fetchMerchantConfig();
        }, refreshInMs);
        this.publishAnalytikaEvent(merchantConfigFetchingCfdScheduled({ time: refreshInMs }));
      } else {
        setTimeout(() => this.fetchMerchantConfig(), configRefreshInterval);
      }
    }
  }
};
